import { getBuses, upsertBus } from '../../../apis/transport/buses';
import { busSchema, parseValidationErrors } from './validators';
import { Bus, BusAction, BusFormValues } from '../types/busTypes';
import { buildUpsertPayload, formatQrPayload } from './payload';
import { SnackbarSeverityType } from '../../../enums';

const fetchBusesForSchool = async (schoolId: string): Promise<Bus[]> => {
  if (!schoolId) return [];
  const response = await getBuses(schoolId);
  const data = (response.data || []) as Bus[];
  return data;
};

const enrolBus = async (
  formValues: BusFormValues,
  schoolId: string
): Promise<{
  ok: boolean;
  errors?: Partial<Record<keyof BusFormValues, string>>;
}> => {
  try {
    await busSchema.validate(formValues, { abortEarly: false });
    await upsertBus({
      name: formValues.name.trim(),
      licensePlateNo: formValues.licensePlateNo.trim(),
      school: schoolId
    });
    return { ok: true };
  } catch (error: any) {
    const fieldErrors = parseValidationErrors(error);
    if (Object.keys(fieldErrors).length)
      return { ok: false, errors: fieldErrors };
    throw error;
  }
};

const updateBus = async (bus: Bus, updates: Partial<Bus>): Promise<void> => {
  const payload = buildUpsertPayload(bus, {
    name: (updates.name || bus.name).trim(),
    licensePlateNo: (updates.licensePlateNo || bus.licensePlateNo).trim()
  });
  await upsertBus(payload);
};

const deleteBusById = async (bus: Bus): Promise<void> => {
  const payload = { id: bus._id || bus.id, isDeleted: true, deviceId: null };
  await upsertBus(payload as any);
};

const unlinkDeviceFromBus = async (bus: Bus): Promise<void> => {
  const payload = { id: bus._id || bus.id, deviceId: null };
  await upsertBus(payload as any);
};

const copyQrBusPayload = async (
  bus: Bus,
  showToast: (message: string, severity: SnackbarSeverityType) => void
): Promise<void> => {
  const busId = bus._id || bus.id || '';
  if (!busId) {
    showToast('Missing Bus Id, cannot copy', SnackbarSeverityType.error);
    return;
  }
  try {
    await navigator.clipboard.writeText(formatQrPayload(busId));
    showToast('Copied to Clipboard', SnackbarSeverityType.success);
  } catch (e) {
    console.error('[copyQrBusPayload] error', e);
    showToast('Failed to copy bus unique id', SnackbarSeverityType.error);
  }
};

const getDialogTitle = (action: BusAction | null): string => {
  switch (action) {
    case BusAction.delete:
      return 'Delete Bus';
    case BusAction.unlink:
      return 'Unlink Bus & Device';
    default:
      return '';
  }
};

const getConfirmationText = (action: BusAction | null): string => {
  switch (action) {
    case BusAction.delete:
      return 'Are you sure you want to delete the bus?';
    case BusAction.unlink:
      return 'Are you sure you want to unlink the device?';
    default:
      return '';
  }
};

const getSuccessMessage = (action: BusAction): string => {
  return action === BusAction.delete ? 'Bus deleted' : 'Device unlinked';
};

const getErrorMessage = (action: BusAction, fallback?: string): string => {
  if (fallback) return fallback;
  return action === BusAction.delete
    ? 'Failed to delete bus'
    : 'Failed to unlink device';
};

const getConfirmButtonLabel = (action: BusAction | null): string => {
  if (action === BusAction.delete) return 'Yes';
  if (action === BusAction.unlink) return 'Ok';
  return 'Confirm';
};

export {
  fetchBusesForSchool,
  enrolBus,
  updateBus,
  deleteBusById,
  unlinkDeviceFromBus,
  copyQrBusPayload,
  getDialogTitle,
  getConfirmationText,
  getSuccessMessage,
  getErrorMessage,
  getConfirmButtonLabel
};
